import React, { useEffect, useState } from 'react';
import { useProfileData } from '../hooks/useProfileData';
import { useTenantData } from '../hooks/useTenantData';
import LoadingSpiner from './Loader/LoadingSpiner';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * AppInitializer component handles loading essential data during app initialization
 * This ensures that profile and tenant data are loaded before the app becomes interactive
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const { isLoading: profileLoading, isError: profileError } = useProfileData();
  const { isLoading: tenantLoading, isError: tenantError } = useTenantData();
  
  const [isInitialized, setIsInitialized] = useState(false);

  // Check if all essential data is loaded
  const isLoading = profileLoading || tenantLoading;
  const hasError = profileError || tenantError;

  useEffect(() => {
    // Mark as initialized when loading is complete
    if (!isLoading) {
      setIsInitialized(true);
    }
  }, [isLoading]);

  // Show loading state while essential data is being fetched
  if (!isInitialized || isLoading) {
    return (
      <div className="app-initializer flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpiner />
          <div className="mt-4 text-gray-600 dark:text-gray-400">
            Initializing application...
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error loading essential data
  if (hasError) {
    return (
      <div className="app-initializer flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Failed to Initialize Application
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            There was an error loading essential application data. Please refresh the page.
          </p>
        </div>
      </div>
    );
  }

  // Render children when initialization is complete
  return <>{children}</>;
};

export default AppInitializer;
